export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`/api${path}`, {
    ...init,
    // sem credentials o cookie de sessão não acompanha a chamada
    credentials: 'include',
    headers: {
      ...(init?.body ? { 'content-type': 'application/json' } : {}),
      ...init?.headers,
    },
  });

  if (res.status === 204) return undefined as T;

  const isJson = res.headers.get('content-type')?.includes('application/json');
  const body = isJson ? await res.json() : await res.text();

  if (!res.ok) {
    const payload = (isJson ? body : { message: body }) as {
      error?: string;
      message?: string;
      details?: unknown;
    };
    throw new ApiError(
      res.status,
      payload.error ?? 'error',
      payload.message ?? 'Não foi possível concluir a ação.',
      payload.details,
    );
  }

  return body as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  put: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  del: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'DELETE', body: body ? JSON.stringify(body) : undefined }),
};

// ------------------------------------------------------------------ tipos

export type Role = 'INTERN' | 'ADMIN';

export interface Me {
  id: string;
  email: string;
  name: string;
  avatarUrl: string | null;
  role: Role;
  dailyTargetMinutes: number;
  workDays: number[];
  timezone: string;
}

export interface Segment {
  id: string;
  kind: 'WORK' | 'BREAK';
  startedAt: string;
  endedAt: string | null;
  description: string | null;
  source: 'CLOCK' | 'MANUAL' | 'ADMIN';
  version: number;
  createdAt: string;
  updatedAt: string;
}

/** Espelha MIN_ACTIVITY_CHARS do servidor. */
export const MIN_ACTIVITY_CHARS = 10;

export interface DayDetail {
  workDate: string;
  segments: Segment[];
  note: { id: string; content: string; version: number } | null;
}

export interface StatusResponse {
  now: string;
  timezone: string;
  today: string;
  /** Datas anteriores a esta estão travadas para lançamento. */
  editableFrom: string;
  day: {
    workDate: string;
    segments: Segment[];
    note: { id: string; content: string; version: number } | null;
  };
}

export interface ReportDay {
  workDate: string;
  weekday: number;
  expected: boolean;
  holiday: string | null;
  targetMinutes: number;
  workedMinutes: number;
  creditMinutes: number;
  creditReason: string | null;
  breakMinutes: number;
  balanceMinutes: number;
  firstIn: string | null;
  lastOut: string | null;
  openNow: boolean;
  note: string | null;
  segments: Array<{
    id: string;
    kind: string;
    start: string;
    end: string | null;
    minutes: number | null;
    description: string | null;
    source: string;
    version: number;
    createdAt: string;
    updatedAt: string;
  }>;
}

export interface ReportUser {
  userId: string;
  name: string;
  email: string;
  avatarUrl: string | null;
  totals: {
    workedMinutes: number;
    creditMinutes: number;
    breakMinutes: number;
    targetMinutes: number;
    balanceMinutes: number;
    daysWorked: number;
    expectedDays: number;
    averageMinutesPerWorkedDay: number;
  };
  days: ReportDay[];
}

export interface TeamMember {
  id: string;
  email: string;
  name: string;
  avatarUrl: string | null;
  role: Role;
  status: 'ACTIVE' | 'INACTIVE';
  dailyTargetMinutes: number;
  workDays: number[];
  createdAt: string;
}

export interface AuditEvent {
  id: string;
  action: string;
  at: string;
  actor: { name: string; email: string; avatarUrl: string | null };
  actorRole: Role | null;
  subject: { name: string; email: string } | null;
  entityType: string;
  entityId: string | null;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  reason: string | null;
  ip: string | null;
  userAgent: string | null;
  requestId: string | null;
}
